import {
  Caption1,
  Input,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Archive16Regular, ArrowRight16Regular, ArrowUndo16Regular, Board16Regular, Delete16Regular, Edit16Regular, LockClosed16Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppDialog, ConfirmDialog, DialogField } from "@/components/app-dialog";
import { FuiButton } from "@/components/fui-control";
import { useDataTableStyles } from "@/components/data-table";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { shellGutter } from "@/shell/content-frame";
import { todayISO } from "@/engine/date";
import type { Project } from "@/engine/types";
import { formatAgo, formatDate } from "@/lib/date-format";
import { planVisibilityOf, showInPlanList, usePlanVisibilityTick } from "@/features/access/plan-visibility";
import { useWorkspaceRole } from "@/features/access/workspace-access";
import { AssigneeAvatars } from "@/features/tasks/task-ui";
import { useCatalogStore } from "@/store/catalog-store";
import { flushPendingSaves, useProjectStore } from "@/store/project-store";
import { isPlanMockId, planMockCards, usePlanMockCase } from "./plan-mock";
import {
  arrangePlans,
  inspectPlan,
  planCover,
  planListScale,
  planMatchesQuery,
  type PlanScope,
  type PlanSnapshot,
  type PlanSort,
  type PlanTone,
  type PlanView,
} from "./plan-gallery";

export type PlanCardItem = {
  id: string;
  name: string;
  updatedAt: string;
  finish: string;
  tone: PlanTone;
  locked: boolean;
  snap: PlanSnapshot;
};

const useStyles = makeStyles({
  frame: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "0%",
    minHeight: 0,
    height: "100%",
  },
  scroller: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "0%",
    minHeight: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: shellGutter,
    paddingRight: shellGutter,
    boxSizing: "border-box",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXL}`,
  },
  tile: {
    position: "relative",
    minWidth: 0,
    ":hover .plan-thumb": {
      borderTopColor: tokens.colorNeutralStroke1,
      borderRightColor: tokens.colorNeutralStroke1,
      borderBottomColor: tokens.colorNeutralStroke1,
      borderLeftColor: tokens.colorNeutralStroke1,
    },
    ":hover .plan-more": {
      opacity: 1,
      pointerEvents: "auto",
    },
    ":focus-within .plan-more": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  open: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    margin: 0,
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: tokens.fontWeightRegular,
    lineHeight: "inherit",
    textAlign: "left",
    cursor: "pointer",
    borderRadius: tokens.borderRadiusLarge,
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "2px",
    },
    ":active .plan-thumb": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  thumb: {
    position: "relative",
    display: "block",
    aspectRatio: "16 / 10",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
    transitionProperty: "border-color, background-color",
    transitionDuration: "120ms",
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  cover: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "4px",
    height: "100%",
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    minWidth: 0,
    boxSizing: "border-box",
  },
  coverStatus: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  coverDone: { color: tokens.colorPaletteGreenForeground1 },
  coverLate: { color: tokens.colorPaletteRedForeground1 },
  coverQuiet: { color: tokens.colorNeutralForeground3 },
  coverDetail: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontVariantNumeric: "tabular-nums",
  },
  more: {
    position: "absolute",
    top: tokens.spacingVerticalS,
    right: tokens.spacingHorizontalS,
    zIndex: 1,
    opacity: 0,
    pointerEvents: "none",
    transitionProperty: "opacity",
    transitionDuration: "120ms",
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "1ms",
    },
  },
  moreBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    padding: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  dangerItem: {
    "&&&": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover:active": { color: tokens.colorPaletteRedForeground1 },
  },
  moreBtnQuiet: {
    border: "none",
    backgroundColor: "transparent",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  meta: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalSNudge,
    minWidth: 0,
    paddingTop: tokens.spacingVerticalS,
  },
  mark: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "16px",
    height: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground3,
  },
  textCol: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
    flexGrow: 1,
    flexShrink: 1,
  },
  nameLine: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  name: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  edited: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  tableFixed: { tableLayout: "fixed", minWidth: "640px" },
  /** 제목 열. 남는 폭을 흡수하고, 오른쪽에 세로선으로 지표 열과 가른다. */
  nameCol: {
    minWidth: "220px",
    width: "100%",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: "hidden",
  },
  nameHead: {
    zIndex: 3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  /** 제목 오른쪽 열. 기본 셀(8px)보다 좌우 여백을 줄여 붙인다. */
  metric: {
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    whiteSpace: "nowrap",
  },
  statusCol: { width: "88px" },
  dateCol: { width: "104px" },
  pctCol: { width: "68px" },
  editedCol: { width: "104px" },
  actionCol: { width: "44px", textAlign: "center" },
  tableRow: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  staticOpen: {
    cursor: "default",
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  nameBtn: { flex: "0 1 auto", minWidth: 0 },
  people: { flexShrink: 0 },
  late: { color: tokens.colorPaletteRedForeground1 },
  quiet: { color: tokens.colorNeutralForeground3 },
  empty: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
  },
});

function writePlan(id: string, patch: Partial<Project>) {
  const live = useProjectStore.getState();
  if (live.activeId === id) {
    if (patch.lifecycle === "active" || patch.lifecycle === "archived") {
      const rest = { ...patch };
      delete rest.lifecycle;
      live.setLifecycle(patch.lifecycle);
      if (Object.keys(rest).length > 0) live.setProject(rest);
      flushPendingSaves();
      return;
    }
    live.setProject(patch);
    flushPendingSaves();
    return;
  }
  useCatalogStore.getState().mutateProject(id, (project) => {
    const next = { ...project, ...patch };
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) delete next[key as keyof Project];
    }
    return next;
  });
}

function PlanCover({ status, detail }: { status: string; detail: string }) {
  const styles = useStyles();
  const tone = status === "완료" ? styles.coverDone : status === "지연" ? styles.coverLate : status === "진행 중" ? undefined : styles.coverQuiet;
  return (
    <span className={styles.cover}>
      <span className={mergeClasses(styles.coverStatus, tone)}>{status}</span>
      {detail ? <span className={styles.coverDetail}>{detail}</span> : null}
    </span>
  );
}

function PlanMenu({
  quiet,
  archived,
  status,
  canEdit,
  onOpen,
  onRename,
  onStatus,
  onArchive,
  onRestore,
  onDelete,
}: {
  quiet?: boolean;
  archived: boolean;
  status: "active" | "done" | "other";
  canEdit: boolean;
  onOpen: () => void;
  onRename: () => void;
  onStatus: (next: "active" | "done") => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const styles = useStyles();
  const checked = status === "other" ? [] : [status];
  return (
    <Menu checkedValues={{ status: checked }} onCheckedValueChange={(_, data) => {
      const next = data.checkedItems[0];
      if (next === "active" || next === "done") onStatus(next);
    }}>
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={mergeClasses(styles.moreBtn, quiet && styles.moreBtnQuiet)} aria-label="플랜 메뉴" onClick={(event) => event.stopPropagation()}>
          <MoreHorizontal16Regular />
        </button>
      </MenuTrigger>
      <MenuPopover onClick={(event) => event.stopPropagation()}>
        <MenuList>
          <MenuItem icon={<ArrowRight16Regular />} onClick={onOpen}>열기</MenuItem>
          {canEdit && !archived ? <MenuItem icon={<Edit16Regular />} onClick={onRename}>수정</MenuItem> : null}
          {canEdit && !archived ? (
            <>
              <MenuDivider />
              <MenuGroup>
                <MenuGroupHeader>상태</MenuGroupHeader>
                <MenuItemRadio name="status" value="active">진행 중</MenuItemRadio>
                <MenuItemRadio name="status" value="done">완료</MenuItemRadio>
              </MenuGroup>
              <MenuDivider />
              <MenuItem icon={<Archive16Regular />} onClick={onArchive}>아카이브</MenuItem>
            </>
          ) : null}
          {canEdit && archived ? <MenuItem icon={<ArrowUndo16Regular />} onClick={onRestore}>복구</MenuItem> : null}
          {canEdit && !archived ? <MenuItem className={styles.dangerItem} icon={<Delete16Regular />} onClick={onDelete}>삭제</MenuItem> : null}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function PlanIdentity({
  name,
  isPrivate,
  people,
  edited,
}: {
  name: string;
  isPrivate: boolean;
  people: string[];
  edited: string;
}) {
  const styles = useStyles();
  return (
    <span className={styles.meta}>
      <span className={styles.mark}>{isPrivate ? <LockClosed16Regular aria-hidden /> : <Board16Regular aria-hidden />}</span>
      <span className={styles.textCol}>
        <span className={styles.nameLine}>
          <Tooltip content={name} relationship="label">
            <span className={styles.name}>{name}</span>
          </Tooltip>
          {people.length > 0 && <AssigneeAvatars names={people} size={20} max={2} />}
        </span>
        <Caption1 className={styles.edited}>{edited}</Caption1>
      </span>
    </span>
  );
}

export function usePlanCards(scope: PlanScope, sort: PlanSort, query: string) {
  const projects = useCatalogStore((s) => s.projects);
  const resources = useCatalogStore((s) => s.resources);
  const previewGuest = useProjectStore((s) => s.previewGuest);
  const workspaceRole = useWorkspaceRole();
  const visibilityTick = usePlanVisibilityTick();
  const guestPlanId = useProjectStore((s) => s.previewGuestPlanId);
  const scenario = usePlanMockCase();
  const today = todayISO();

  const list = useMemo(() => {
    const rows = useCatalogStore.getState().list();
    if (previewGuest) return guestPlanId ? rows.filter((row) => row.id === guestPlanId) : [];
    return rows.filter((row) => showInPlanList(planVisibilityOf(row.id), workspaceRole).shown);
  }, [projects, previewGuest, guestPlanId, workspaceRole, visibilityTick]);

  const resourceNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const resource of Object.values(resources)) names[resource.id] = resource.displayName;
    return names;
  }, [resources]);

  const snapshots = useMemo(() => {
    const map = new Map<string, PlanSnapshot>();
    for (const row of list) {
      const project = projects[row.id];
      if (project) map.set(row.id, inspectPlan(project, today, resourceNames));
    }
    return map;
  }, [list, projects, today, resourceNames]);

  const catalog = useMemo(() => {
    if (scenario === "empty") return [];
    if (scenario === "long") return planMockCards(today);
    return list.flatMap((row) => {
      const snap = snapshots.get(row.id);
      if (!snap || snap.lifecycle === "trashed") return [];
      return [{ id: row.id, name: row.name || "이름 없는 플랜", updatedAt: row.updatedAt, finish: snap.finish, tone: snap.tone, locked: planVisibilityOf(row.id) === "private", snap }];
    });
  }, [scenario, today, list, snapshots]);

  const cards = useMemo(() => {
    const matched = catalog.filter((card) => planMatchesQuery(card.name, query));
    return arrangePlans(matched, scope, sort).filter((card) => scope === "all" || card.snap.lifecycle === "active" || card.snap.lifecycle === "done");
  }, [catalog, scope, sort, query]);

  return { hasPlans: catalog.length > 0, cards, scale: planListScale(catalog.map((card) => card.snap)) };
}

function listedStatus(status: string): "active" | "done" | "other" {
  if (status === "완료") return "done";
  if (status === "진행 중") return "active";
  return "other";
}

export function PlanCardGrid({ view, cards, empty }: { view: PlanView; cards: PlanCardItem[]; empty: "search" | "scope" }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const deleteProject = useCatalogStore((s) => s.deleteProject);
  const activeId = useProjectStore((s) => s.activeId);
  const closeProject = useProjectStore((s) => s.closeProject);
  const canEdit = !useProjectStore((s) => s.previewGuest);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [archiveId, setArchiveId] = useState<string | null>(null);
  const [rename, setRename] = useState<{ id: string; name: string } | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const today = todayISO();

  const openPlan = (id: string, tab: string) => {
    if (tab === "board") {
      navigate(`/projects/${id}/tasks?view=board`);
      return;
    }
    navigate(`/projects/${id}/${tab}`);
  };

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteProject(deleteId);
    if (activeId === deleteId) closeProject();
    setDeleteId(null);
  };

  const confirmArchive = () => {
    if (!archiveId) return;
    writePlan(archiveId, { lifecycle: "archived" });
    setArchiveId(null);
  };

  const saveRename = () => {
    const name = renameValue.trim();
    if (!rename || !name) return;
    writePlan(rename.id, { name });
    setRename(null);
  };

  const menuFor = (card: PlanCardItem) => {
    const cover = planCover(card.snap, today);
    return {
      archived: card.snap.lifecycle === "archived",
      status: listedStatus(cover.status),
      canEdit,
      onOpen: () => openPlan(card.id, "schedule"),
      onRename: () => {
        setRename({ id: card.id, name: card.name });
        setRenameValue(card.name);
      },
      onStatus: (next: "active" | "done") => writePlan(card.id, { statusOverride: next }),
      onArchive: () => setArchiveId(card.id),
      onRestore: () => writePlan(card.id, { lifecycle: "active" }),
      onDelete: () => setDeleteId(card.id),
    };
  };

  return (
    <div className={styles.frame}>
      <div className={styles.scroller}>
        {cards.length === 0 ? (
          <div className={styles.empty}>
            <GhostEmptyState
              variant="search"
              title={empty === "search" ? "찾는 플랜이 없습니다" : "해당하는 플랜이 없습니다"}
              description={empty === "search" ? "다른 이름으로 찾아 보세요." : "다른 상태로 바꿔 보세요."}
            />
          </div>
        ) : view === "grid" ? (
          <div className={styles.grid}>
            {cards.map((card) => {
              const preview = isPlanMockId(card.id);
              return (
                <PlanTile
                  key={card.id}
                  locked={card.locked}
                  name={card.name}
                  updatedAt={card.updatedAt}
                  snap={card.snap}
                  today={today}
                  onOpen={preview ? undefined : () => openPlan(card.id, "schedule")}
                  menu={preview ? undefined : menuFor(card)}
                />
              );
            })}
          </div>
        ) : (
          <PlanTable cards={cards} today={today} menuFor={menuFor} onOpen={(id) => openPlan(id, "schedule")} />
        )}
      </div>
      <ConfirmDialog
        open={deleteId != null}
        title="플랜 삭제"
        message="플랜을 삭제할까요? 일정과 태스크 등 모든 데이터가 함께 삭제됩니다."
        confirmLabel="삭제"
        onCancel={() => setDeleteId(null)}
        onConfirm={confirmDelete}
      />
      <ConfirmDialog
        open={archiveId != null}
        title="플랜 아카이브"
        message="이 플랜을 아카이브할까요? 읽기 전용이 되고, 언제든 복구할 수 있습니다."
        confirmLabel="아카이브"
        onCancel={() => setArchiveId(null)}
        onConfirm={confirmArchive}
      />
      <AppDialog
        open={rename != null}
        onClose={() => setRename(null)}
        width="sm"
        title="이름 수정"
        footer={
          <>
            <FuiButton appearance="outline" density="dialog" onClick={() => setRename(null)}>
              취소
            </FuiButton>
            <FuiButton appearance="primary" density="dialog" disabled={!renameValue.trim()} onClick={saveRename}>
              저장
            </FuiButton>
          </>
        }
      >
        <DialogField label="이름">
          <Input
            size="small"
            value={renameValue}
            onChange={(_, data) => setRenameValue(data.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveRename();
            }}
          />
        </DialogField>
      </AppDialog>
    </div>
  );
}

function planLabel(name: string, isPrivate: boolean, cover: { status: string; detail: string }, edited: string) {
  const bits = [name];
  if (isPrivate) bits.push("비공개");
  bits.push(cover.status);
  if (cover.detail) bits.push(cover.detail);
  bits.push(edited);
  return bits.join(", ");
}

function PlanTile({
  locked,
  name,
  updatedAt,
  snap,
  today,
  onOpen,
  menu,
}: {
  locked: boolean;
  name: string;
  updatedAt: string;
  snap: PlanSnapshot;
  today: string;
  onOpen?: () => void;
  menu?: Omit<Parameters<typeof PlanMenu>[0], "quiet">;
}) {
  const styles = useStyles();
  const edited = `${formatAgo(updatedAt)} 편집됨`;
  const cover = planCover(snap, today);
  const body = (
    <>
      <span className={mergeClasses(styles.thumb, "plan-thumb")}>
        <PlanCover status={cover.status} detail={cover.detail} />
      </span>
      <PlanIdentity name={name} isPrivate={locked} people={snap.people} edited={edited} />
    </>
  );
  return (
    <article className={styles.tile}>
      {onOpen ? (
        <button type="button" className={styles.open} aria-label={planLabel(name, locked, cover, edited)} onClick={onOpen}>
          {body}
        </button>
      ) : (
        <div className={mergeClasses(styles.open, styles.staticOpen)}>{body}</div>
      )}
      {menu ? (
        <div className={mergeClasses(styles.more, "plan-more")}>
          <PlanMenu {...menu} />
        </div>
      ) : null}
    </article>
  );
}

function PlanTable({
  cards,
  today,
  onOpen,
  menuFor,
}: {
  cards: PlanCardItem[];
  today: string;
  onOpen: (id: string) => void;
  menuFor: (card: PlanCardItem) => Omit<Parameters<typeof PlanMenu>[0], "quiet">;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  return (
    <div className={table.frame}>
      <table className={mergeClasses(table.table, styles.tableFixed)} aria-label="플랜">
        <thead>
          <tr>
            <th className={mergeClasses(table.th, styles.nameCol, styles.nameHead)} scope="col">플랜</th>
            <th className={mergeClasses(table.th, styles.metric, styles.statusCol)} scope="col">상태</th>
            <th className={mergeClasses(table.th, styles.metric, table.num, styles.dateCol)} scope="col">종료</th>
            <th className={mergeClasses(table.th, styles.metric, table.num, styles.pctCol)} scope="col">진척</th>
            <th className={mergeClasses(table.th, styles.metric, styles.editedCol)} scope="col">편집</th>
            <th className={mergeClasses(table.th, styles.metric, styles.actionCol)} scope="col" aria-label="동작" />
          </tr>
        </thead>
        <tbody>
          {cards.map((card) => {
            const preview = isPlanMockId(card.id);
            const cover = planCover(card.snap, today);
            const scheduled = card.tone !== "empty";
            const statusTone = cover.status === "지연" ? styles.late : cover.status === "진행 중" ? undefined : styles.quiet;
            const open = () => onOpen(card.id);
            return (
              <tr key={card.id} className={preview ? undefined : styles.tableRow} onClick={preview ? undefined : open}>
                <td className={mergeClasses(table.td, styles.nameCol)}>
                  <span className={styles.nameCell}>
                    <span className={styles.mark}>{card.locked ? <LockClosed16Regular aria-hidden /> : <Board16Regular aria-hidden />}</span>
                    <Tooltip content={card.name} relationship="label">
                      {preview ? (
                        <span className={mergeClasses(table.linkBtn, styles.nameBtn, styles.staticOpen)}>{card.name}</span>
                      ) : (
                        <button
                          type="button"
                          className={mergeClasses(table.linkBtn, styles.nameBtn)}
                          aria-label={planLabel(card.name, card.locked, cover, `${formatAgo(card.updatedAt)} 편집됨`)}
                          onClick={(event) => {
                            event.stopPropagation();
                            open();
                          }}
                        >
                          {card.name}
                        </button>
                      )}
                    </Tooltip>
                    {card.snap.people.length > 0 && (
                      <span className={styles.people}>
                        <AssigneeAvatars names={card.snap.people} size={20} max={3} />
                      </span>
                    )}
                  </span>
                </td>
                <td className={mergeClasses(table.td, styles.metric, statusTone)}>{cover.status}</td>
                <td className={mergeClasses(table.td, styles.metric, table.num)}>{scheduled && card.finish ? formatDate(card.finish, "numeric") : "—"}</td>
                <td className={mergeClasses(table.td, styles.metric, table.num)}>{scheduled ? `${card.snap.pct}%` : "—"}</td>
                <td className={mergeClasses(table.td, styles.metric)}>{formatAgo(card.updatedAt)}</td>
                <td className={mergeClasses(table.td, styles.metric, styles.actionCol)}>
                  {preview ? <span className={styles.quiet}>—</span> : <PlanMenu quiet {...menuFor(card)} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
